import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * The Skin Ritual (sections/rbl-ritual-set.liquid): adds the chosen powder and the skincare, each with its 4-week
 * selling plan, in one request, then announces it the same way product-form.js does so the cart drawer opens and
 * every cart section re-renders. The total and saving follow the chosen powder.
 */
class RblRitual extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#update);
    this.querySelector('[data-add]')?.addEventListener('click', this.#add);
    this.#update();
  }

  get #choice() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
  }

  #update = () => {
    const choice = this.#choice;
    if (!choice) return;
    const total = this.querySelector('[data-total-out]');
    const saving = this.querySelector('[data-saving-out]');
    if (total) total.textContent = choice.dataset.total ?? '';
    if (saving) saving.textContent = choice.dataset.saving ?? '';
    for (const pack of this.querySelectorAll('[data-pack]')) {
      /** @type {HTMLElement} */ (pack).hidden = pack.getAttribute('data-pack') !== choice.value;
    }
  };

  #add = async (event) => {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const choice = this.#choice;
    if (!choice || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const items = [
      { id: Number(choice.dataset.variant), quantity: 1, selling_plan: Number(choice.dataset.plan) },
      { id: Number(this.dataset.skinVariant), quantity: 1, selling_plan: Number(this.dataset.skinPlan) },
    ];
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      let response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Could not add the ritual');

      // The ritual's own discount code (data-code): applied to the cart so the drawer and checkout show it.
      if (this.dataset.code) {
        const updated = await fetch(`${Theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            discount: this.dataset.code,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        if (!updated.status) response = updated;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 2,
          sections: response.sections ?? undefined,
          source: 'rbl-ritual',
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: String(error), code: 'INVALID', detail: {} }));
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = 'Something went wrong adding the ritual. Please try again.';
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('rbl-ritual')) customElements.define('rbl-ritual', RblRitual);
